import { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Card, CardContent, CardHeader, CardActions, Button, Checkbox, FormGroup, FormControlLabel, Typography } from "@mui/material"

export default function AccountPatSettings({ account, patSettings, update, add }) {
  // Explicit `true` only: an absent or unexpected value must read as disabled rather
  // than telling an admin the feature is on when the server never said so.
  const patEnabled = patSettings?.patEnabled === true
  // Kept as override state (not derived directly from the prop) so that saving or
  // switching accounts is safe: `reloadData: "patSettings"` drops this module back to
  // LOADING, unmounting the view and clearing `patOverride`, so it remounts from the
  // freshly-loaded `patSettings` rather than carrying over a stale checkbox value. That
  // unmount is scoped to this module, so it can't discard a sibling card's unsaved edits.
  const [patOverride, setPatOverride] = useState(null)
  const allowPats = patOverride === null ? patEnabled : patOverride

  const savePatSettings = useCallback(() => {
    update(`/api/${account}/pat-settings`, { patEnabled: allowPats }, account, {
      verb: "PUT",
      reloadData: "patSettings",
    }, (error) => {
      if (error) {
        add("error", "Unknown Error", "An unknown error has happened. Please try again later.")
      } else {
        add("success", "Token Settings Updated", "Personal access token settings updated successfully")
      }
    })
  }, [account, add, update, allowPats])

  return <Card className="modules_account-pat-settings" style={{ maxWidth: 400, margin: "1rem auto" }}>
    <CardHeader title="Personal Access Tokens" />
    <CardContent>
      <Typography variant="body2" component="p">
        Personal access tokens let members authenticate to the Omega Point API and connect third-party tools as themselves.
        Turning this off blocks members of this account from creating, revealing, or using their own tokens here; existing
        member tokens stop working for this account but are not deleted. This does not affect the AI agent, which uses its
        own tokens and keeps working either way.
      </Typography>
      <br />
      <FormGroup row>
        <FormControlLabel
          control={<Checkbox checked={allowPats} onChange={event => { setPatOverride(event.target.checked) }} />}
          label="Allow members to create and use personal access tokens"
        />
      </FormGroup>
    </CardContent>
    <CardActions style={{ flexDirection: "row-reverse" }}>
      <Button
        color="primary"
        disabled={allowPats === patEnabled}
        onClick={savePatSettings}
      >
        Save Token Settings
      </Button>
    </CardActions>
  </Card>
}

AccountPatSettings.propTypes = {
  account: PropTypes.string.isRequired,
  patSettings: PropTypes.shape({
    patEnabled: PropTypes.bool,
  }),
  update: PropTypes.func.isRequired,
  add: PropTypes.func.isRequired,
}
